import React, { useState, ReactNode } from 'react'

import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

import { Url, Id } from '@/types'
import { CardSize } from '@/types/ui'
import { type CardProps } from '@components/Card'
import Favorite from '@components/Favorite'
import { Icon, type IconProps } from '@components/Icon'
import { Image, type ImageProps } from '@components/Image'
import Placeholder from '@components/Placeholder'
import Tag from '@components/Tag'
import { trans, SHOW_MORE } from '@helpers/translator'
import useFavorite from '@hooks/useFavorite'

// Définir les variantes de classe pour la position
const positionVariants = tv({
  base: 'absolute top-0 z-10',
  variants: {
    size: {
      sm: 'left-4',
      md: 'left-6',
      lg: 'left-8',
      xl: 'left-10',
      default: 'left-6',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const imageVariants = tv({
  base: 'absolute inset-0 z-0 object-cover h-full w-full transition-transform duration-[800ms] ease-in-out group-hover:scale-[110%]',
  variants: {
    overlayPosition: {
      top: 'group-hover:translate-y-[5%]',
      bottom: 'group-hover:-translate-y-[5%]',
      left: 'group-hover:-translate-x-[5%]',
    },
    hasDescription: {
      true: 'group-hover:-translate-y-[5%]',
    },
  },
  compoundVariants: [
    {
      hasDescription: true,
      overlayPosition: 'top',
      className: 'group-hover:translate-y-[5%]',
    },
  ],
  defaultVariants: {
    overlayPosition: 'top',
    hasDescription: false,
  },
})

interface CardContentProps {
  title?: string
  children: ReactNode
  className?: string
  id: Id
  backgroundColor?: string
  image?: ImageProps
  displayFlag?: boolean
  displayFavorite?: boolean
  aspectRatio?: string
  url?: Url
  size?: CardSize
  tag?: string
  overlayPosition?: 'top' | 'bottom' | 'left'
  hasDescription?: boolean
}

const CardContent: React.FC<CardContentProps> = ({
  title = trans(SHOW_MORE),
  children,
  className,
  id,
  backgroundColor,
  image,
  displayFlag = false,
  displayFavorite = false,
  aspectRatio = 'aspect-square',
  url,
  size = 'default',
  tag,
  overlayPosition,
  hasDescription,
}) => {
  const { isFavorite, toggleFavorite } = useFavorite(id)
  return (
    <div className="relative overflow-hidden">
      {url && (
        <a
          href={url.href}
          className="inset-0 absolute z-[10] cursor-pointer"
          target={url.target}
          rel="noopener noreferrer nofollow"
          aria-label={url.title || title}
        >
          <span className="sr-only">{url.title || title}</span>
        </a>
      )}
      <div
        className={twMerge(
          `flex flex-col gap-y-2 relative h-full w-full`,
          aspectRatio,
          className,
        )}
      >
        {tag && (
          <Tag className="absolute top-[19px] ltr:left-[21px] rtl:right-[21px] z-30">
            {tag}
          </Tag>
        )}
        {displayFlag && (
          <Icon
            name="monaco_flag_mini"
            size="auto"
            className={positionVariants({
              size: size,
            })}
          />
        )}
        {displayFavorite && (
          <Favorite
            isFavorite={isFavorite}
            className="absolute ltr:right-2 rtl:left-2 top-2 z-30"
            onClick={toggleFavorite}
          />
        )}
        {backgroundColor && (
          <div
            className={twMerge(
              'absolute inset-0 z-0 h-full w-full',
              backgroundColor,
            )}
          />
        )}

        {image && image.src ? (
          <div className="absolute inset-0 z-0 h-full w-full overflow-hidden">
            <Image
              src={image.src}
              alt={image.alt}
              focalPoint={image.focalPoint}
              srcset={image.srcset}
              className={imageVariants({
                overlayPosition: overlayPosition || 'bottom',
                hasDescription: hasDescription,
              })}
            />
          </div>
        ) : (
          <Placeholder />
        )}
        {children}
      </div>
    </div>
  )
}

export default CardContent
